import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, select, spawn, takeLatest } from 'redux-saga/effects';

import {
  Address,
  ConfigureAddOnFailureReason,
  ContactMethodLabels,
  GetScrubbedAddressPayload,
  Mutation,
  PhoneType,
  ProductInstanceStatus,
  Query,
  SubmitFullOrderInput,
} from '@mfe/shared/schema-types';

import { refetchCurrentAddOns } from '../addOns';
import { CONFIGURE_ADDON } from '../addOns/queriesAndMutations';
import { selectUser } from '../auth';
import {
  FetchWithErrorsMutation,
  FetchWithErrorsQuery,
  graphqlMutation,
  graphqlMutationWithErrors,
  graphqlQuery,
  graphqlQueryWithErrors,
} from '../graphql';
import { refetchUserInfo, selectUserInfo } from '../userInfo';
import {
  GET_ALL_DETAILS,
  GET_INVOICE_HISTORY,
  GET_SCRUBBED_ADDRESS,
  GET_STREAM_ELIGIBILITY,
  SUBMIT_FULL_ORDER,
} from './requests';
import {
  addOrderAttempts,
  getAllDetails as getAllDetailsAction,
  setConfigureStream as getConfigureStreamAction,
  getInvoiceHistory as getInvoiceHistoryAction,
  getStreamEligibility,
  scrubAddress as scrubAddressAction,
  selectStreamOnPromo,
  setConfigureStreamError,
  setConfigureStreamSuccess,
  setConfirmModal,
  setCurrentPage,
  setEditAddress,
  setInvoiceHistory,
  setInvoiceHistoryLoading,
  setIsEditingAddress,
  setIsLoading,
  setIsOrderSubmitting,
  setIsScrubbingLoading,
  setOfferDetails,
  setOfferDetailsLoading,
  setOrderAttempts,
  setOrderFail,
  setOrderSucceeded,
  setScrubLocation,
  setShippingAddress,
  setStreamEligibility,
  setStreamError,
  setUserDetails,
  submitFullOrder as submitFullOrderAction,
} from './streamOnPromoSlice';
import { setUsagePageStatistics } from '../utils';
import { waitForToken } from '../utils/utilsSagas';
import { ConfigureStream, Pages, resetOrderAttempts } from './types';

const areAddressesEqual = (addressA: GetScrubbedAddressPayload, addressB: Address): boolean =>
  addressA.addressLines[0] === addressB.addressLines[0] &&
  addressA.addressLines[1] === addressB.addressLines[1] &&
  addressA.countryCode === addressB.countryCode &&
  addressA.municipality === addressB.municipality &&
  addressA.postalCode === addressB.postalCode &&
  addressA.region === addressB.region;

const sanitizeAddressLines = (address: Address) => {
  const { addressLines } = address;
  if (addressLines[1] === null || addressLines[1] === undefined)
    return { ...address, addressLines: [addressLines[0], ''] };
  return address;
};

function* checkStreamEligibility() {
  const {
    user: { productInstanceStatus },
  } = yield select(selectUser);

  if (productInstanceStatus !== ProductInstanceStatus.Active) {
    return;
  }

  yield put(setIsLoading(true));

  yield call(waitForToken);

  const apiResponse: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
    query: GET_STREAM_ELIGIBILITY,
    fetchPolicy: 'no-cache',
  });

  const { data, errors, runtimeError } = apiResponse;

  if (runtimeError || errors) {
    yield put(setStreamError(runtimeError ?? errors));
    yield put(setIsLoading(false));
    return;
  }

  if (data?.checkStreamEligibility) {
    const isEligible = data?.checkStreamEligibility?.isEligible ?? false;
    yield put(setStreamEligibility({ isEligible }));
  }

  yield put(setIsLoading(false));
}

function* getInvoiceHistory() {
  yield put(setInvoiceHistoryLoading(true));
  yield put(setIsLoading(true));

  const getInvoiceData: Query = yield call(graphqlQuery, {
    query: GET_INVOICE_HISTORY,
  });

  yield put(setInvoiceHistory(getInvoiceData.getInvoiceHistory?.currentMonthlyBill));

  yield put(setIsLoading(false));
  yield put(setInvoiceHistoryLoading(false));
}

function* getAllDetails() {
  yield put(setOfferDetailsLoading(true));
  yield put(setUsagePageStatistics());

  const { offerDetails }: ReturnType<typeof selectStreamOnPromo> = yield select(selectStreamOnPromo);

  try {
    const allDetailsData: Query = yield call(graphqlQuery, {
      query: GET_ALL_DETAILS,
      fetchPolicy: !offerDetails.productTypeId ? 'no-cache' : undefined,
    });

    const offerDetailsResult = allDetailsData.getOfferDetails;

    if (!offerDetailsResult) {
      yield put(setCurrentPage(Pages.error));
      yield put(setOfferDetailsLoading(false));
      return;
    }

    yield put(setOfferDetails(offerDetailsResult));
    yield put(setUserDetails(allDetailsData.getUserDetails));

    const shippingAddress = allDetailsData.getUserDetails?.shippingAddress;
    if (shippingAddress) {
      yield put(setShippingAddress(shippingAddress));
    }
  } catch (e) {
    console.error(e);
    yield put(setCurrentPage(Pages.error));
    yield put(setOfferDetailsLoading(false));
  }
}

function* configureStreamSaga(action: PayloadAction<ConfigureStream>) {
  const input = action.payload;

  const apiResponse: FetchWithErrorsMutation = yield call(graphqlMutationWithErrors, {
    mutation: CONFIGURE_ADDON,
    variables: { input },
    fetchPolicy: !input.shoppingCartId ? 'no-cache' : undefined,
  });

  const { data, errors, runtimeError } = apiResponse;

  if (errors || runtimeError) {
    yield put(setConfigureStreamError(runtimeError ?? errors));
  }

  if (data && data.configureAddon) {
    yield put(setConfigureStreamSuccess(data.configureAddon.success));
    if (!data?.configureAddon?.success && data?.configureAddon?.message === ConfigureAddOnFailureReason.ExtendCart) {
      yield put(setConfigureStreamError({ message: 'Unable to extend cart' }));
    }
  } else {
    yield put(setConfigureStreamError({ message: 'Unable to configure addon' }));
  }
}

function* submitFullOrder({ payload }: { type: string; payload: SubmitFullOrderInput }) {
  const {
    userInfo: { phone },
  }: ReturnType<typeof selectUserInfo> = yield select(selectUserInfo);

  yield put(setIsOrderSubmitting(true));
  yield put(setOrderFail(null));
  yield put(addOrderAttempts());

  try {
    const input = {
      address: {
        ...payload.address,
        labels: [ContactMethodLabels.Shipping],
      },
      isEditingNumber: payload.phoneNumber
        ? phone[phone.mobile as PhoneType]?.phoneNumber !== payload.phoneNumber
        : false,
      phoneNumber: payload?.phoneNumber?.replace(/[\s()-]/g, ''),
    };

    const submitOrderData: Mutation = yield call(graphqlMutation, {
      mutation: SUBMIT_FULL_ORDER,
      variables: {
        input,
      },
      fetchPolicy: 'no-cache',
    });

    if (!submitOrderData?.submitFullOrder?.didOrderFail) {
      yield put(refetchUserInfo());
      yield put(getStreamEligibility());
      yield put(setCurrentPage(Pages.orderSummary));
      yield put(setOrderAttempts(resetOrderAttempts));
      yield put(setOrderSucceeded(true));
      yield put(refetchCurrentAddOns(submitOrderData?.submitFullOrder?.orderId as string));
    } else {
      yield put(setOrderFail(submitOrderData?.submitFullOrder?.error?.failureReason ?? ''));
      yield put(setCurrentPage(Pages.error));
    }
    yield put(setIsOrderSubmitting(false));
  } catch (e) {
    yield put(setIsOrderSubmitting(false));
    yield put(setOrderFail(JSON.stringify(e) ?? ''));
    yield put(setCurrentPage(Pages.error));
  }
}

function* scrubAddress({ payload }: { type: string; payload: { address: Address | null } }) {
  const address = payload?.address as Address;

  yield put(setIsScrubbingLoading(true));
  if (address.countryCode === '') address.countryCode = 'US';

  try {
    const data: Query = yield call(graphqlQuery, {
      query: GET_SCRUBBED_ADDRESS,
      variables: {
        address: { ...sanitizeAddressLines(address) },
      },
    });
    const scrubbedAddress = data.getScrubbedAddress;

    if (!scrubbedAddress) {
      yield put(setScrubLocation(null));
      yield put(setConfirmModal(true));
      yield put(setIsScrubbingLoading(false));
      return;
    }

    const { shippingAddress }: ReturnType<typeof selectStreamOnPromo> = yield select(selectStreamOnPromo);

    const scrubbedShippingAddress: Address = {
      addressLines: scrubbedAddress.addressLines,
      countryCode: scrubbedAddress.countryCode,
      municipality: scrubbedAddress.municipality,
      postalCode: scrubbedAddress.postalCode,
      region: scrubbedAddress.region,
    };

    yield put(setScrubLocation(scrubbedShippingAddress));

    const result = areAddressesEqual(scrubbedAddress, shippingAddress);

    if (!result) {
      yield put(setConfirmModal(true));
      yield put(setIsScrubbingLoading(false));
    } else {
      yield put(setIsEditingAddress(false));
      yield put(setShippingAddress(scrubbedShippingAddress));
      yield put(
        setEditAddress({
          addressLines: ['', ''],
          postalCode: '',
          countryCode: '',
          municipality: '',
          region: '',
        })
      );
    }

    yield put(setIsScrubbingLoading(false));
  } catch (e) {
    yield put(setIsScrubbingLoading(false));
    yield put(setCurrentPage(Pages.error));
  }
}

export function* watchStreamOnPromo() {
  yield spawn(function* () {
    yield all([
      takeLatest(getStreamEligibility.type, checkStreamEligibility),
      takeLatest(getInvoiceHistoryAction.type, getInvoiceHistory),
      takeLatest(getAllDetailsAction.type, getAllDetails),
      takeLatest(submitFullOrderAction.type, submitFullOrder),
      takeLatest(scrubAddressAction.type, scrubAddress),
      takeLatest(getConfigureStreamAction.type, configureStreamSaga),
    ]);
  });
}
